import { EnvService } from '@aipb/gulliver/env/util';
import { FrameRepository, FrameService } from '@aipb/gulliver/frame/data-access';
import { RouteItem } from '@aipb/gulliver/frame/model';
import { AuthService } from '@aipb/shared/auth';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { RouterRepository } from '@ngneat/elf-ng-router-store';
import { combineLatest, startWith } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  SidebarActionListComponent,
  SidebarActionMenuComponent,
  SidebarCategoryRouteListComponent,
  SidebarFooterComponent,
  SidebarHeaderComponent,
  SidebarRouteListComponent,
  SidebarRouteMenuComponent
} from '../../components';

@Component({
  selector: 'fr-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatDividerModule,
    MatListModule,
    MatMenuModule,
    SidebarHeaderComponent,
    SidebarCategoryRouteListComponent,
    SidebarRouteListComponent,
    SidebarActionListComponent,
    SidebarActionMenuComponent,
    SidebarFooterComponent,
    SidebarRouteMenuComponent,
    TranslocoModule
  ]
})
export class SidebarComponent {
  frameService = inject(FrameService);
  frameRepository = inject(FrameRepository);
  transloco = inject(TranslocoService);
  env = inject(EnvService);
  routerRepository = inject(RouterRepository);
  auth = inject(AuthService);

  expanded$ = this.frameRepository.sidebarExpanded$;
  version$ = this.frameRepository.version$;
  dashboard$ = combineLatest([this.frameRepository.activeApp$, this.routerRepository.selectParams('configId')]).pipe(
    map(([activeApp, configId]) => {
      if (configId && configId in this.env.raw.applications[activeApp]) {
        return this.env.raw.applications[activeApp][configId]?.dashboard;
      }

      return undefined;
    })
  );
  hasMenu$ = this.frameRepository.applications$.pipe(
    map(
      (applications) =>
        applications.filter(
          (app) =>
            !app.hideInMenu &&
            app.id in this.auth.appRequiredRoles &&
            this.auth.appRequiredRoles[app.id].every((role) => this.auth.hasRole(role))
        ).length > 1
    )
  );

  staticRoutes$ = combineLatest([
    this.frameRepository.staticRoutes$,
    this.dashboard$,
    this.frameRepository.activeApp$,
    this.transloco.langChanges$.pipe(startWith(this.transloco.getActiveLang())),
    this.routerRepository.selectParams('configId'),
    this.routerRepository.selectQueryParams()
  ]).pipe(
    map(([staticRoutes, dashboard, activeApp, lang, configId, queryParams]): RouteItem[] => {
      const menuTitle = dashboard?.menuTitle;
      const menuIcon = dashboard?.menuIcon;
      const ignoredApps = ['browser-mirador', 'dynamic-gallery'];

      if (ignoredApps.includes(activeApp)) {
        return staticRoutes;
      }

      let label = menuTitle && lang in menuTitle ? menuTitle[lang] : 'ui.titles.dashboard_default';

      let icon = menuIcon || 'home';

      return [
        {
          url: `/${activeApp}/${configId}/dashboard`,
          label: label,
          deletable: false,
          htmlId: 'menudashboard',
          queryParams,
          icon: icon
        },
        ...staticRoutes
      ];
    })
  );
  dynamicRouteCategoryItems$ = this.frameRepository.dynamicRouteCategoryItems$;
  actions$ = this.frameRepository.actions$;
  activeApplication$ = this.frameRepository.activeApplication$;

  get currentLang(): string {
    return this.transloco.getActiveLang();
  }

  onRemoveRoute(evt: MouseEvent, route: RouteItem): void {
    evt.stopPropagation();
    evt.preventDefault();
    this.frameService.removeRoute(route);
  }

  onToggleExpand(): void {
    this.frameService.toggleSidebarExpand();
  }
}
